import { useEffect, useState } from 'react';
import { ModalLayout } from 'components';
import {
  Form,
  Input,
  Select,
  Divider,
  Table,
  Button,
  InputNumber,
  Checkbox,
} from 'antd';
import { nanoid } from 'nanoid';
import MessageAlert from 'utils';

/**----------------------------------------
 * [Component] 총관리자: 상품등록 > 옵션등록
 * ----------------------------------------
 */
const ProductOptionAppendModal = ({
  isOpen,
  initialData,
  onClose,
  onSave,
  onModify,
}) => {
  /* ===== STATE ===== */
  const [rows, setRows] = useState([]);
  const [optionTitle, setOptionTitle] = useState('');
  const [optionType, setOptionType] = useState('SELECT');
  const [optionRequired, setOptionRequired] = useState('REQUIRED');

  /* ===== HOOKS ===== */
  useEffect(() => {
    if (!isOpen) {
      cleanOptionState();
      return;
    }

    if (isOpen && initialData) {
      setOptionTitle(initialData.option_title);
      setOptionType(initialData.option_type);
      setOptionRequired(initialData.type);

      setRows(
        initialData.option.map((item) => ({
          key: nanoid(),
          option_name: item.option_name,
          option_price: item.option_price,
          default: item.default,
        }))
      );
    }
  }, [isOpen, initialData]);

  /* ===== FUNCTIONS ===== */
  /**
   *
   * --
   */
  const handleAddRow = () => {
    const newRow = {
      key: nanoid(),
      option_title: '',
      option_price: 0,
      default: false,
    };
    setRows((prevData) => [...prevData, newRow]);
  };

  /**
   *
   * --
   */
  const handleInputChange = (key, field, value) => {
    setRows((prevData) =>
      prevData.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  /**
   *
   * --
   */
  const handleDeleteRow = (key) => {
    setRows((prevData) => prevData.filter((item) => item.key !== key));
  };

  /**--------------------
   * 옵션 생성
   * --------------------
   */
  const handleSave = () => {
    const { status, type } = checkOptionValidation();
    if (!status) {
      MessageAlert.warning(
        type === 'VALUE'
          ? '필수 값이 입력되지 않았습니다.'
          : '하위 옵션은 필수입니다.'
      );
      return;
    }

    if (onSave) {
      onSave({
        option_id: nanoid(),
        option_title: optionTitle,
        option_type: optionType,
        type: optionRequired,
        order: 1,
        option: rows.map((item) => ({
          option_name: item.option_name,
          option_price: item.option_price,
          default: item.defaultValue,
        })),
      });
    }
  };

  /**--------------------
   * 옵션수정 저장
   * --------------------
   */
  const handleModify = () => {
    const { status, type } = checkOptionValidation();

    if (!status) {
      MessageAlert.warning(
        type === 'VALUE'
          ? '필수 값이 입력되지 않았습니다.'
          : '하위 옵션은 필수입니다.'
      );
      return;
    }

    if (onModify) {
      onModify({
        option_id: initialData.option_id,
        option_title: optionTitle,
        option_type: optionType,
        type: optionRequired,
        order: 1,
        option: rows.map((item) => ({
          option_name: item.option_name,
          option_price: item.option_price,
          default: item.default,
        })),
      });
    }
  };

  /**--------------------
   * 초기화
   * --------------------
   */
  const cleanOptionState = () => {
    setRows([]);
    setOptionTitle('');
    setOptionType('SELECT');
    setOptionRequired('REQUIRED');
  };

  /**--------------------
   * 필수값 체크(예외처리)
   * --------------------
   */
  const checkOptionValidation = () => {
    if (optionTitle === '') {
      return { status: false, type: 'VALUE' };
    }

    if (!rows || rows.length === 0) {
      return { status: false, type: 'OPTION' };
    }

    const invalidChildOption = rows.find((row) => {
      /*  
      UPDATE: 
      [25.02.04] 옵션 기본금액 0원 허용
      */
      if (
        row.option_name === '' ||
        row.option_name === undefined ||
        row.option_name === null
        // !row.option_price
      ) {
        return true;
      }

      return false;
    });

    if (invalidChildOption) {
      return { status: false, type: 'VALUE' };
    }

    return { status: true, type: 'VALUE' };
  };

  /* ===== RENDER ===== */
  return (
    <ModalLayout
      type={'modal'}
      // width={660}
      open={isOpen}
      onCancel={() => onClose()}
      centered
      closable
      title={'옵션 설정'}
      width={700}
      footer={
        <div style={{ display: 'flex', justifyContent: 'right' }}>
          {initialData ? (
            <Button onClick={handleModify}>수정</Button>
          ) : (
            <Button onClick={handleSave}>등록</Button>
          )}
        </div>
      }
    >
      <Divider style={{ margin: '15px 0' }} />
      <Form layout="vertical">
        <Form.Item label="옵션명">
          <Input
            value={optionTitle}
            onChange={(e) => setOptionTitle(e.target.value)}
          />
        </Form.Item>
        <Form.Item label="필수옵션 설정">
          <Select
            value={optionRequired}
            onChange={(value) => setOptionRequired(value)}
            options={[
              { label: '필수 옵션', value: 'REQUIRED' },
              { label: '추가 옵션', value: 'ADDITION' },
            ]}
          />
        </Form.Item>
        {/* <Form.Item label="옵션타입">
          <Select 
            value={optionType}
            onChange={(value) => setOptionType(value)}
            options={[
              { label: '추가옵션', value: 'COUNT' },
              { label: '선택옵션', value: 'CHOICE' },
            ]}
          />
        </Form.Item> */}
      </Form>
      <Divider />
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'right',
        }}
      >
        <Button onClick={() => handleAddRow()}>하위옵션 추가</Button>
      </div>
      <div style={{ height: '230px', overflow: 'auto' }}>
        <Table
          dataSource={rows}
          pagination={false}
          columns={[
            {
              title: '옵션항목',
              dataIndex: 'option_name',
              key: 'option_name',
              render: (_, record) => (
                <Input
                  value={record.option_name}
                  onChange={(e) =>
                    handleInputChange(record.key, 'option_name', e.target.value)
                  }
                />
              ),
            },
            {
              title: '옵션항목 금액',
              dataIndex: 'option_price',
              key: 'option_price',
              render: (_, record) => (
                <InputNumber
                  style={{ width: 150 }}
                  value={record.option_price}
                  formatter={(value) =>
                    `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                  min={0}
                  onChange={(value) =>
                    handleInputChange(record.key, 'option_price', value ?? 0)
                  }
                />
              ),
            },
            {
              title: '기본값',
              dataIndex: 'default',
              key: 'default',
              render: (_, record) => (
                <Checkbox
                  checked={record.default}
                  onChange={(e) =>
                    handleInputChange(record.key, 'default', e.target.checked)
                  }
                />
              ),
            },
            {
              title: '삭제',
              dataIndex: 'delete',
              key: 'delete',
              render: (_, record) => (
                <Button
                  color="danger"
                  onClick={() => handleDeleteRow(record.key)}
                >
                  삭제
                </Button>
              ),
            },
          ]}
        />
      </div>
    </ModalLayout>
  );
};

export default ProductOptionAppendModal;
